import ReceiptFlow from "../receipts/ReceiptFlow";
import { restoreReceipt } from "../../lib/receipt-draft";
import { useEffect, useState } from "react";
import { ArrowLeft, PenLine, ScanLine, Sparkles } from "lucide-react";
import ExpenseForm from "./ExpenseForm";
import BatchExpenseReview from "./BatchExpenseReview";
import { readBatch } from "./batch-save";
import type { DashboardData, ExpenseInput, Owner } from "../../types";
import { todayIn } from "../../lib/finance";
import EntrySaved from "./EntrySaved";
import SmartAddChat from "./SmartAddChat";
import { restoreSmartDraft, smartDraftKey } from "./smart-add-draft";
import type { EntryPreset } from "./use-entry-modal";
import { getDefaultTripAccount, getDefaultTripToAccount } from "../../lib/trips";

export default function ExpenseEntry({
  data,
  profile,
  onSaved,
  preset,
  online = true,
}: {
  data: DashboardData;
  profile: Owner;
  onSaved: () => Promise<DashboardData | undefined>;
  preset?: EntryPreset;
  online?: boolean;
}) {
  const key = smartDraftKey(profile);
  const [draft, setDraft] = useState(() => {
    const draft = restoreSmartDraft(key),
      batch = readBatch(profile);
    return batch
      ? {
          ...draft,
          stage: "review" as const,
          expenses: batch.map((i) => i.expense),
        }
      : draft;
  });
  const [manualSequence, setManualSequence] = useState(0);
  const [mode, setMode] = useState<"smart" | "manual" | "receipt">(() => {
    if (readBatch(profile)) return "smart";
    if (restoreReceipt(profile)) return "receipt";
    if (!online || !navigator.onLine) return "manual";
    if (preset) return "manual";
    return "smart";
  });
  useEffect(() => {
    if (!online && mode === "smart" && !readBatch(profile)) setMode("manual");
  }, [online, mode, profile]);
  const preferredAccount =
    data.accounts?.find(
      (a) =>
        a.active !== false && a.owner === profile && /primary/i.test(a.name),
    ) ||
    data.accounts?.find((a) => a.active !== false && a.owner === profile) ||
    data.accounts?.[0];
  const presetTrip = preset?.tripId ? data.trips?.find((t) => t.id === preset.tripId) : undefined;
  const defaultTripAcc = presetTrip
    ? getDefaultTripAccount(presetTrip, data.accounts || [], preset?.tripDestination, data.trips, preset?.type)
    : undefined;
  const defaultTripToAcc = presetTrip && preset?.type === "Transfer"
    ? getDefaultTripToAccount(presetTrip, data.accounts || [], preset?.accountId || defaultTripAcc?.id, preset?.tripDestination, data.trips)
    : undefined;

  const presetInput: ExpenseInput | undefined =
    preset
      ? {
          requestId: crypto.randomUUID(),
          accountId: preset.accountId || defaultTripAcc?.id || preferredAccount?.id || "",
          toAccountId: preset.toAccountId || defaultTripToAcc?.id,
          exchangeRate: preset.exchangeRate,
          type: preset.type || "Expense",
          amount: preset.amount || "",
          date: todayIn(data.timezone),
          category: preset.category || "Uncategorized",
          subcategory: preset.subcategory || "",
          title: preset.title || (preset.type === "Transfer" ? "Account transfer" : ""),
          notes: preset.notes || "",
          tripId: preset.tripId,
          tripDestination: preset.tripDestination,
        }
      : undefined;
  const [pending, setPending] = useState(false),
    [saved, setSaved] = useState(false);
  const [storageNotice, setStorageNotice] = useState(false);
  const [chatError, setChatError] = useState(""),
    [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (saved) return;
    try {
      localStorage.setItem(
        key,
        JSON.stringify({ ...draft, updated: Date.now() }),
      );
    } catch {
      setStorageNotice(true);
    }
  }, [draft, key, saved]);
  const [savedAccounts, setSavedAccounts] = useState<DashboardData["accounts"] | null>(null);
  const [balanceFailed, setBalanceFailed] = useState(false);
  async function done(accountIds: string[] = []) {
    setSaved(true);
    try {
      localStorage.removeItem(key);
    } catch {
      /* Saving remains confirmed even if storage fails. */
    }
    const refreshed = await onSaved();
    if (refreshed) setSavedAccounts(refreshed.accounts.filter(a => accountIds.includes(a.id)));
    else setBalanceFailed(true);
  }
  const reviewing =
    mode === "smart" && draft.stage === "review" && draft.expenses.length > 0;
  if (saved)
    return <EntrySaved count={draft.expenses.length} savedAccounts={savedAccounts} balanceFailed={balanceFailed} />;
  return (
    <div className="expense-entry">
      {!saved && (
        <div className="entry-tabs" aria-label="Entry method">
          <button
            type="button"
            aria-pressed={mode === "smart"}
            disabled={pending || !online}
            title={online ? undefined : "Available when online"}
            onClick={() => setMode("smart")}
          >
            <Sparkles size={16} />
            Smart Add
          </button>
          <button
            type="button"
            aria-pressed={mode === "receipt"}
            disabled={pending}
            onClick={() => setMode("receipt")}
          >
            <ScanLine size={16} />
            Receipt
          </button>
          <button
            type="button"
            aria-pressed={mode === "manual"}
            disabled={pending}
            onClick={() => setMode("manual")}
          >
            <PenLine size={16} />
            Manual
          </button>
        </div>
      )}
      {storageNotice && !saved && (
        <p className="smart-caption" role="status">
          Draft storage is unavailable in this browser. Keep this panel open
          until you finish.
        </p>
      )}
      {mode === "receipt" ? (
        <ReceiptFlow
          data={data}
          profile={profile}
          onSaved={done}
          onPendingChange={setPending}
        />
      ) : mode === "manual" || reviewing ? (
        <>
          {reviewing && !saved && (
            <button
              type="button"
              className="text-button smart-back"
              disabled={pending}
              onClick={() => setDraft((v) => ({ ...v, stage: "chat" }))}
            >
              <ArrowLeft size={16} />
              Back to conversation
            </button>
          )}
          {reviewing ? (
            <BatchExpenseReview
              data={data}
              profile={profile}
              expenses={draft.expenses}
              onPendingChange={setPending}
              onExpensesChange={(expenses) =>
                setDraft((v) => ({
                  ...v,
                  expenses,
                  stage: expenses.length ? "review" : "chat",
                }))
              }
              onSaved={done}
            />
          ) : (
            <ExpenseForm
              key={manualSequence}
              data={data}
              profile={profile}
              initialInput={presetInput}
              onPendingChange={setPending}
              onSaved={done}
              onAddAnother={() => { setPending(false); setManualSequence(value => value + 1); }}
            />
          )}
        </>
      ) : (
        <SmartAddChat
          data={data}
          profile={profile}
          draft={draft}
          setDraft={setDraft}
          error={chatError}
          setError={setChatError}
          expanded={expanded}
          setExpanded={setExpanded}
          onPendingChange={setPending}
        />
      )}
    </div>
  );
}
